// Owns the active location (localStorage key 'group'). Every write goes through
// here. A page load never changes the active id; only switchGroup does, and it
// always reloads or navigates with a full page load, which groupDataCache relies on.
import { reactive } from 'vue'
import axios from 'axios'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import { exitAppendMode } from '@/util/appendMode'

export interface GroupSummary {
  id: number
  name: string
  position: number
  city: string | null
  state: string | null
  country: string | null
  code: string | null
  company_number: string | null
  img: string | null
  copy_status: string | null
  default_group: number | null
  email_domain: string | null
  email_dns_alert: number
  suppressed_staff_count: number
}

const GROUP_KEY = 'group'
export const LAST_GROUP_KEY = 'lastGroupId'
export const RECENT_KEY = 'recentGroupIds'
const RECENT_MAX = 5

export const state = reactive({
  groups: [] as GroupSummary[],
  loaded: false,
  switching: false,
  switchingToName: '',
  // Search palette, rendered once at layout level (GroupSwitcher variant palette-host).
  paletteOpen: false,
  externalChange: null as null | { oldName: string; newName: string },
})

function parse(raw: string | null): any | null {
  try {
    return JSON.parse(raw || 'null')
  } catch (e) {
    return null
  }
}

function idOf(g: any): number | null {
  const id = Number(g?.id)
  return Number.isFinite(id) && id > 0 ? id : null
}

export function getActiveGroup(): any | null {
  return parse(localStorage.getItem(GROUP_KEY))
}

export function getActiveGroupId(): number | null {
  return idOf(getActiveGroup())
}

// The location this tab rendered. Set at module load; set once more by the first
// write when the tab started without one (sign-in inside the SPA).
let renderedGroupId: number | null = getActiveGroupId()
let renderedGroupName: string = getActiveGroup()?.name || ''

export function getRenderedGroupId(): number | null {
  return renderedGroupId
}

export function getRenderedGroupName(): string {
  return renderedGroupName
}

export function writeActiveGroup(full: any): void {
  localStorage.setItem(GROUP_KEY, JSON.stringify(full))
  localStorage.setItem(LAST_GROUP_KEY, String(full.id))
  if (renderedGroupId === null) {
    renderedGroupId = idOf(full)
    renderedGroupName = full?.name || ''
  }
}

export function patchActiveGroup(fields: Record<string, any>): void {
  const g = getActiveGroup()
  if (!g) return
  localStorage.setItem(GROUP_KEY, JSON.stringify({ ...g, ...fields, id: g.id }))
}

export function getRecentGroupIds(): number[] {
  const v = parse(localStorage.getItem(RECENT_KEY))
  if (!Array.isArray(v)) return []
  return v.map(Number).filter((n: number) => Number.isInteger(n) && n > 0)
}

export function pushRecentGroupId(id: number): void {
  const next = [id, ...getRecentGroupIds().filter(x => x !== id)].slice(0, RECENT_MAX)
  localStorage.setItem(RECENT_KEY, JSON.stringify(next))
}

export function onlyReady<T extends { copy_status?: string | null }>(list: T[]): T[] {
  return list.filter(g => !g.copy_status || g.copy_status === 'ready')
}

export function pickLoginGroupId(list: GroupSummary[], lastGroupId: number | null): number | null {
  if (!list.length) return null
  if (lastGroupId && list.some(g => g.id === lastGroupId)) return lastGroupId
  const def = list.find(g => g.default_group === g.id)
  return def ? def.id : list[0].id
}

export type SyncOutcome =
  | { status: 'ok' }
  | { status: 'replaced'; newId: number; oldName: string }
  | { status: 'none' }

export function decideSync(list: GroupSummary[]): SyncOutcome {
  const cachedId = getActiveGroupId()
  if (cachedId && list.some(g => g.id === cachedId)) return { status: 'ok' }
  const newId = pickLoginGroupId(list, null)
  if (!newId) return { status: 'none' }
  return { status: 'replaced', newId, oldName: getActiveGroup()?.name || '' }
}

async function authConfig(): Promise<any> {
  const s = await Auth.currentSession()
  return { headers: { Authorization: s.getIdToken().getJwtToken() } }
}

function companyId(): string {
  return localStorage.getItem('companyId') || ''
}

export async function fetchGroupSummaries(): Promise<GroupSummary[]> {
  const r = await axios.get(API_LOCATION + 'companies/' + companyId() + '/groups?view=summary', await authConfig())
  return onlyReady(r.data || [])
}

export async function fetchFullGroup(id: number): Promise<any | null> {
  const r = await axios.get(API_LOCATION + 'companies/' + companyId() + '/groups?group_id=' + id, await authConfig())
  return (r.data && r.data[0]) || null
}

// Loads the switcher list once per page load into state.groups.
let listPromise: Promise<GroupSummary[]> | null = null

export function loadGroupList(): Promise<GroupSummary[]> {
  if (!listPromise) {
    listPromise = (async () => {
      try {
        const list = await fetchGroupSummaries()
        state.groups = list
        state.loaded = true
        return list
      } catch (e) {
        listPromise = null
        throw e
      }
    })()
  }
  return listPromise
}

// Refresh the cached row for the same id. Never changes the id, and writes nothing if
// another tab switched location while the fetch was in flight (writing would undo that
// switch).
export async function refreshActiveGroup(): Promise<void> {
  const id = getActiveGroupId()
  if (!id) return
  const full = await fetchFullGroup(id)
  if (full && Number(full.id) === id && getActiveGroupId() === id) writeActiveGroup(full)
}

// Replace a cached location that is no longer in the user's list. Returns false when the
// replacement could not be loaded, or when another tab changed the location meanwhile
// (that tab's choice stands).
export async function replaceActiveGroup(newId: number): Promise<boolean> {
  const before = getActiveGroupId()
  const full = await fetchFullGroup(newId)
  if (!full || getActiveGroupId() !== before) return false
  writeActiveGroup(full)
  return true
}

let inFlight: Promise<void> | null = null

// Page navigation, swappable in tests (jsdom cannot navigate).
let nav = {
  pathname: (): string => window.location.pathname,
  reload: (): void => window.location.reload(),
  assign: (url: string): void => window.location.assign(url),
}

export function isSwitching(): boolean {
  return inFlight !== null
}

// Editors whose ?id= is one of several records per location. After a switch the old
// id belongs to the previous location, so the switch lands on the list instead.
export const SWITCH_REDIRECTS: Record<string, string> = {
  '/bookings/settings': '/bookings/settings/list',
  '/waivers/settings': '/waivers/types',
}

export function switchGroup(id: number, name: string, opts: { redirectTo?: string } = {}): Promise<void> {
  if (inFlight) return inFlight
  if (id === getActiveGroupId() && !opts.redirectTo) return Promise.resolve()
  inFlight = (async () => {
    state.switching = true
    state.switchingToName = name
    try {
      exitAppendMode()
      const cfg = { ...(await authConfig()), timeout: 3000, allowCrossGroup: true }
      try {
        await axios.put(API_LOCATION + 'companies/' + companyId() + '/groups/' + id + '/default', {}, cfg)
      } catch (e: any) {
        const status = e?.response?.status
        if (status === 401 || status === 403) throw e
        // The default only seeds new browsers; a slow or failed write must not block the switch.
        console.warn('[activeGroup] default write skipped', status || e?.code)
      }
      const full = await fetchFullGroup(id)
      if (!full) throw new Error('GROUP_NOT_FOUND')
      writeActiveGroup(full)
      pushRecentGroupId(id)
      const target = opts.redirectTo || SWITCH_REDIRECTS[nav.pathname()]
      if (target) nav.assign(target)
      else nav.reload()
    } catch (e) {
      state.switching = false
      state.switchingToName = ''
      inFlight = null
      throw e
    }
  })()
  return inFlight
}

// Another tab changed the location. This tab never reloads on its own: the console
// cannot tell whether a page (a drawer, a full-page editor) holds unsaved work, so it
// shows the "Location changed" dialog and the request guard refuses writes until reload.
// 'ignore' when nothing changed for this tab, or while this tab is switching itself.
export function decideExternalChange(newId: number | null, switchingNow: boolean): 'ignore' | 'dialog' | 'clear' {
  if (switchingNow || newId === null) return 'ignore'
  if (newId === renderedGroupId) return 'clear' // switched back to this tab's location
  return 'dialog'
}

// The storage event fires only in other tabs, never in the tab that wrote.
let watching = false

export function startCrossTabWatch(): void {
  if (watching) return
  watching = true
  window.addEventListener('storage', (e: StorageEvent) => {
    if (e.key !== GROUP_KEY) return
    const next = parse(e.newValue)
    const d = decideExternalChange(idOf(next), inFlight !== null)
    if (d === 'clear') state.externalChange = null
    if (d === 'dialog') state.externalChange = { oldName: renderedGroupName, newName: next?.name || '' }
  })
}

// Sign out happens without a page load, so everything tied to the signed-in user is
// dropped here; the next sign in in this tab starts clean.
export function resetActiveGroupSession(): void {
  renderedGroupId = null
  renderedGroupName = ''
  inFlight = null
  listPromise = null
  state.groups = []
  state.loaded = false
  state.paletteOpen = false
  state.switching = false
  state.switchingToName = ''
  state.externalChange = null
}

// Test hooks only.
export function __setNavigationForTests(n: typeof nav): void {
  nav = n
}

export function __resetForTests(): void {
  renderedGroupId = getActiveGroupId()
  renderedGroupName = getActiveGroup()?.name || ''
  inFlight = null
  listPromise = null
  state.groups = []
  state.loaded = false
  state.paletteOpen = false
  state.switching = false
  state.switchingToName = ''
  state.externalChange = null
}
